<template>
  <div class="ai-sidebar-root">
    <!-- ====== 悬浮唤起按钮 ====== -->
    <div
      v-if="!visible && !picking && showLauncher"
      class="ai-launcher"
      title="AI 编辑助手"
      @click="open()"
    >
      <i class="el-icon-magic-stick"></i>
      <span class="ai-launcher-text">AI</span>
    </div>

    <!-- ====== 选取模式:顶部操作条 ====== -->
    <div v-if="picking" class="ai-pickbar">
      <i class="el-icon-aim"></i>
      <span>移动鼠标高亮区域或图片，点击选取；可再次选取继续添加</span>
      <span class="ai-pickbar-count" v-if="pickedCountInSession > 0">已选 {{ pickedCountInSession }}</span>
      <button class="ai-pickbar-done" @mousedown.stop @click.stop="stopPicking">完成 (Esc)</button>
    </div>

    <!-- ====== 选取模式:高亮框 ====== -->
    <div
      v-show="picking && highlight.visible"
      class="ai-pick-highlight"
      :style="{ top: highlight.top + 'px', left: highlight.left + 'px', width: highlight.width + 'px', height: highlight.height + 'px' }"
    >
      <span class="ai-pick-highlight-label">
        <i :class="highlight.isImage ? 'el-icon-picture-outline' : 'el-icon-document'"></i>
        {{ highlight.label }}
      </span>
    </div>

    <!-- ====== 侧边栏主体 ====== -->
    <transition name="ai-slide">
      <div v-show="visible && !picking" class="ai-panel">
        <!-- 头部 -->
        <div class="ai-panel-header">
          <div class="ai-panel-title">
            <i class="el-icon-magic-stick"></i>
            AI 编辑助手
            <span class="ai-model-tag">多模态编辑 #{{ modelId }}</span>
            <span v-if="mockOn" class="ai-model-tag" style="background:#fff7ed;color:#c2410c;">MOCK 模拟</span>
          </div>
          <div class="ai-panel-header-actions">
            <i class="el-icon-delete" title="清空当前会话" @click="clearMessages"></i>
            <i class="el-icon-close" title="收起" @click="close"></i>
          </div>
        </div>

        <!-- 会话 Tabs:并发编辑多处,后台会话任务继续运行 -->
        <div class="ai-tabs">
          <div
            v-for="sx in sessions"
            :key="sx.id"
            class="ai-tab"
            :class="{ 'is-active': sx.id === activeSessionId }"
            :title="sx.title"
            @click="switchSession(sx.id)"
          >
            <i v-if="sessionBusy(sx)" class="el-icon-loading ai-tab-busy"></i>
            <span class="ai-tab-title">{{ sx.title }}</span>
            <i v-if="sessions.length > 1" class="el-icon-close ai-tab-close" @click.stop="closeSession(sx.id)"></i>
          </div>
          <i v-if="sessions.length < 6" class="el-icon-plus ai-tab-add" title="新建会话(并发编辑另一处)" @click="addSession"></i>
        </div>

        <!-- 消息列表 -->
        <div ref="msgList" class="ai-msg-list">
          <div v-if="!messages.length" class="ai-msg-empty">
            <p>我可以帮你改分镜脚本、视频提示词、角色/场景描述,也能分析选中的图片。</p>
            <p class="ai-msg-empty-tips">
              点击下方「<i class="el-icon-aim"></i> 选取区域」拾取页面内容(任意图片也可直接点选),<br />
              输入框里用 <b>@</b> 可引用角色 / 场景 / 道具资产。
            </p>
          </div>

          <div
            v-for="msg in messages"
            :key="msg.id"
            class="ai-msg"
            :class="'ai-msg--' + msg.role"
          >
            <div class="ai-msg-bubble">
              <!-- 用户消息附带的上下文摘要 -->
              <div v-if="msg.role === 'user' && msg.contextSummary" class="ai-msg-ctx">{{ msg.contextSummary }}</div>

              <!-- 流式中:展示原始增量输出 -->
              <template v-if="msg.streaming">
                <div class="ai-msg-streaming"><i class="el-icon-loading"></i> 正在生成...</div>
                <div v-if="msg.rawText" class="ai-msg-raw">{{ msg.rawText }}</div>
              </template>

              <!-- 完成:展示解析后的回复 -->
              <template v-else>
                <div v-if="msg.text" class="ai-msg-text">{{ msg.text }}</div>
                <div v-if="msg.error" class="ai-msg-error"><i class="el-icon-warning-outline"></i> {{ msg.error }}</div>

                <!-- 修改建议卡片 -->
                <div v-for="(edit, ei) in msg.edits || []" :key="msg.id + '-edit-' + ei" class="ai-edit-card">
                  <div class="ai-edit-card-head">
                    <span class="ai-edit-card-target"><i class="el-icon-position"></i> {{ edit.regionLabel }}</span>
                    <span v-if="edit.applied" class="ai-edit-applied"><i class="el-icon-check"></i> 已应用</span>
                  </div>
                  <div class="ai-edit-card-content" :class="{ 'is-collapsed': !edit.expanded && edit.content.length > 180 }">{{ edit.content }}</div>
                  <div class="ai-edit-card-actions">
                    <el-button
                      v-if="edit.content.length > 180"
                      size="mini"
                      type="text"
                      @click="$set(edit, 'expanded', !edit.expanded)"
                    >{{ edit.expanded ? '收起' : '展开全文' }}</el-button>
                    <el-button size="mini" type="text" icon="el-icon-copy-document" @click="copyText(edit.content)">复制</el-button>
                    <el-button
                      v-if="edit.canApply"
                      size="mini"
                      type="primary"
                      plain
                      :loading="edit.applying"
                      :disabled="edit.applied || edit.applying"
                      @click="applyEdit(edit)"
                    >{{ edit.applied ? '已应用' : '应用修改' }}</el-button>
                    <el-tooltip v-else content="该区域未提供写回入口,可复制后手动粘贴" placement="top">
                      <span class="ai-edit-noapply"><i class="el-icon-info"></i> 不可直接写回</span>
                    </el-tooltip>
                  </div>
                </div>

                <!-- 图像编辑指令卡片(intent=edit_image) -->
                <div v-if="msg.imageEdit" class="ai-imgedit-card">
                  <div class="ai-imgedit-head">
                    <span><i class="el-icon-picture-outline"></i> 图像编辑指令</span>
                    <span class="ai-imgedit-model">模型:{{ msg.imageEdit.modelLabel || '素材编辑' }}</span>
                  </div>
                  <div class="ai-imgedit-body">
                    <img :src="msg.imageEdit.image" class="ai-imgedit-thumb" />
                    <div class="ai-imgedit-instruction">{{ msg.imageEdit.instruction }}</div>
                  </div>
                  <div v-if="msg.imageEdit.status === 'done' && msg.imageEdit.resultUrl" class="ai-imgedit-result">
                    <div class="ai-imgedit-compare">
                      <div class="ai-imgedit-compare-item">
                        <span class="ai-imgedit-compare-label">原图</span>
                        <img :src="msg.imageEdit.image" class="ai-imgedit-compare-img" />
                      </div>
                      <div class="ai-imgedit-compare-arrow"><i class="el-icon-right"></i></div>
                      <div class="ai-imgedit-compare-item">
                        <span class="ai-imgedit-compare-label">生成结果</span>
                        <img :src="msg.imageEdit.resultUrl" class="ai-imgedit-compare-img" />
                      </div>
                    </div>
                    <div class="ai-imgedit-result-bar">
                      <span class="ai-imgedit-done"><i class="el-icon-check"></i> {{ msg.imageEdit.replaced ? '已替换原图' : '编辑完成' }}</span>
                      <span class="ai-imgedit-result-actions">
                        <el-button
                          size="mini"
                          type="text"
                          icon="el-icon-refresh"
                          title="以当前生效的图为输入,再次执行该模型(生成新卡片,本卡保留)"
                          @click="regenerateImageTask(msg.imageEdit)"
                        >重新生成</el-button>
                        <el-button size="mini" type="text" icon="el-icon-copy-document" @click="copyText(msg.imageEdit.resultUrl)">复制图片链接</el-button>
                        <el-button
                          v-if="msg.imageEdit.sourceEl || msg.imageEdit.applyTarget || msg.imageEdit.image"
                          size="mini"
                          type="primary"
                          plain
                          :loading="msg.imageEdit.replacing"
                          :disabled="msg.imageEdit.replaced || msg.imageEdit.replacing"
                          @click="applyImageResult(msg.imageEdit)"
                        >{{ msg.imageEdit.replaced ? '已替换' : '应用替换' }}</el-button>
                      </span>
                    </div>
                  </div>
                  <div v-if="msg.imageEdit.error" class="ai-msg-error"><i class="el-icon-warning-outline"></i> {{ msg.imageEdit.error }}</div>
                  <div class="ai-imgedit-actions">
                    <el-button
                      size="mini"
                      type="primary"
                      plain
                      :loading="msg.imageEdit.status === 'running'"
                      :disabled="msg.imageEdit.status === 'running' || msg.imageEdit.status === 'done'"
                      @click="runImageEdit(msg.imageEdit)"
                    >{{ msg.imageEdit.status === 'done' ? '已完成' : (msg.imageEdit.status === 'running' ? '编辑中…' : (msg.imageEdit.status === 'failed' ? '重试编辑' : '开始编辑')) }}</el-button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- 上下文 chips -->
        <div v-if="contextChips.length" class="ai-chips">
          <div
            v-for="chip in contextChips"
            :key="chip.key"
            class="ai-chip"
            :class="'ai-chip--' + chip.kind"
            :title="chipTitle(chip)"
          >
            <img v-if="chip.image" :src="chip.image" class="ai-chip-img" />
            <i v-else :class="chipIcon(chip)"></i>
            <span class="ai-chip-label">{{ chip.label }}</span>
            <i
              v-if="chip.text"
              class="el-icon-bottom-left ai-chip-insert"
              title="把内容填入输入框"
              @click.stop="insertChipText(chip)"
            ></i>
            <i
              v-if="chip.kind === 'region' && chip.canApply"
              :class="chip.applying ? 'el-icon-loading' : 'el-icon-top-right'"
              class="ai-chip-writeback"
              title="把输入框当前内容回填到该区域（走原保存链路）"
              @click.stop="writeBackChip(chip)"
            ></i>
            <i class="el-icon-close ai-chip-remove" @click.stop="removeChip(chip)"></i>
          </div>
          <span class="ai-chips-clear" @click="contextChips = []">清空</span>
        </div>

        <!-- 图片快捷模型标签(选中图片后出现;直点调用,不走意图路由) -->
        <div v-if="quickImageTarget" class="ai-quick-models">
          <img :src="quickImageTarget.image" class="ai-quick-target" :title="'目标图:' + (quickImageTarget.label || '选中图片')" />
          <span class="ai-quick-tag" :class="{ 'is-busy': quickBusy.enhance }" @click="runQuickEnhance">★ 选择我可以强化真人效果</span>
          <span class="ai-quick-tag" :class="{ 'is-active': cnFormVisible, 'is-busy': quickBusy.cn }" @click="toggleCnForm">★ 选择我生成国人高质量真人图</span>
        </div>
        <div v-if="quickImageTarget && cnFormVisible" class="ai-quick-cnform">
          <el-input v-model="cnForm.name" size="mini" placeholder="姓名(可选)" class="ai-cn-name"></el-input>
          <el-input v-model="cnForm.age" size="mini" placeholder="年龄*" class="ai-cn-age"></el-input>
          <el-radio-group v-model="cnForm.gender" size="mini">
            <el-radio-button label="女">女</el-radio-button>
            <el-radio-button label="男">男</el-radio-button>
          </el-radio-group>
          <el-button size="mini" type="primary" plain :disabled="!cnForm.age || !cnForm.gender" @click="runQuickCnEnhance">开始</el-button>
          <el-button size="mini" type="text" @click="cnFormVisible = false">取消</el-button>
        </div>

        <!-- @资产 下拉 -->
        <div v-if="mention.visible" class="ai-mention">
          <div v-if="!mentionList.length" class="ai-mention-empty">当前页面没有可引用的资产</div>
          <div
            v-for="(item, idx) in mentionList"
            :key="item.type + '-' + item.id"
            class="ai-mention-item"
            :class="{ active: idx === mention.activeIndex }"
            @mousedown.prevent="selectMention(item)"
            @mouseenter="mention.activeIndex = idx"
          >
            <img v-if="item.avatar" :src="item.avatar" class="ai-mention-avatar" />
            <span v-else class="ai-mention-avatar ai-mention-avatar--ph"><i class="el-icon-user"></i></span>
            <span class="ai-mention-type">{{ assetTypeLabel(item.type) }}</span>
            <span class="ai-mention-name">{{ item.name }}</span>
            <span v-if="item.sub" class="ai-mention-sub">{{ item.sub }}</span>
          </div>
        </div>

        <!-- 输入区 -->
        <div class="ai-input-area">
          <div class="ai-input-toolbar">
            <el-button size="mini" :type="picking ? 'primary' : 'default'" icon="el-icon-aim" @click="togglePicking">
              {{ picking ? '退出选取' : '选取区域' }}
            </el-button>
            <span class="ai-input-hint">@ 引用资产 · Enter 发送 · Shift+Enter 换行</span>
          </div>
          <div class="ai-input-row">
            <textarea
              ref="input"
              v-model="inputText"
              class="ai-input"
              rows="3"
              placeholder="说说你想怎么改,或先选取一块区域..."
              @keydown="onInputKeydown"
              @input="onInputChanged"
              @blur="onInputBlur"
            ></textarea>
            <el-button
              class="ai-send-btn"
              type="primary"
              size="small"
              :loading="sending"
              :disabled="sending || (!inputText.trim() && !contextChips.length)"
              @click="send"
            >发送</el-button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getCreateModels, createGenerateTask, getGenerateTask } from '@/api/c/create';
import { updateScriptCharacter, updateScriptCharacterForm, updateScriptLocation } from '@/api/c/scripts';
import { resolveImageApplyTarget, findApplyTargetByImage } from '@/utils/aiAssistant';
import {
  AI_ASSISTANT_CONFIG,
  AI_ASSET_TYPE_LABEL,
  aiBus,
  isAiMockEnabled,
  findAiRegionFromPoint,
  findPickableImageFromPoint,
  resolveAiRegion,
  refreshAiRegion,
  isAiRegionAlive,
  collectAiAssets,
  buildAssistantContent,
  callAiAssistant,
  parseAssistantOutput,
} from '@/utils/aiAssistant';

const STORAGE_KEY = 'ai_sidebar_messages_v1';
let _chipSeq = 0;
let _msgSeq = 0;
let _sessionSeq = 0;

export default {
  name: 'AiSidebar',
  data() {
    return {
      visible: false,
      picking: false,
      /** 多会话并发:每个会话独立 {id,title,messages,chips,input,sending,picked};后台会话的流式回复/图片任务继续运行 */
      sessions: [],
      activeSessionId: '',
      highlight: { visible: false, top: 0, left: 0, width: 0, height: 0, label: '', isImage: false },
      mention: { visible: false, query: '', activeIndex: 0, triggerStart: -1 },
      assetCache: [],
      // 图片快捷模型标签(强化真人效果 id=97 / 国人高质量真人 id=119)
      quickBusy: { enhance: false, cn: false },
      cnFormVisible: false,
      cnForm: { name: '', age: '', gender: '' },
      modelId: AI_ASSISTANT_CONFIG.MODEL_ID,
      mockOn: false,
      hideOnPaths: ['/login'],
    };
  },
  computed: {
    activeSession() {
      return this.sessions.find((sx) => sx.id === this.activeSessionId) || this.sessions[0] || null;
    },
    /* 以下五个为活动会话字段的代理,既有代码(含模板 v-model)零改动直通 */
    messages: {
      get() { const sx = this.activeSession; return sx ? sx.messages : []; },
      set(v) { const sx = this.activeSession; if (sx) sx.messages = v || []; },
    },
    contextChips: {
      get() { const sx = this.activeSession; return sx ? sx.chips : []; },
      set(v) { const sx = this.activeSession; if (sx) sx.chips = v || []; },
    },
    inputText: {
      get() { const sx = this.activeSession; return sx ? sx.input : ''; },
      set(v) { const sx = this.activeSession; if (sx) sx.input = v; },
    },
    sending: {
      get() { const sx = this.activeSession; return !!(sx && sx.sending); },
      set(v) { const sx = this.activeSession; if (sx) sx.sending = !!v; },
    },
    pickedCountInSession: {
      get() { const sx = this.activeSession; return sx ? sx.picked : 0; },
      set(v) { const sx = this.activeSession; if (sx) sx.picked = v || 0; },
    },
    /** 快捷模型标签的目标图:仅对角色类型显示(强化真人/国人高质量仅适用于角色) */
    quickImageTarget() {
      const isCharChip = (c) => c.type === 'asset-char' || c.type === 'char' || (c.applyTarget && c.applyTarget.kind === 'role');
      let fallback = null;
      for (let i = this.contextChips.length - 1; i >= 0; i--) {
        const c = this.contextChips[i];
        if (!c || !c.image || !isCharChip(c)) continue;
        // 优先选取带写回标识的 region chip
        if (c.kind === 'region' && (c.applyTarget || c.el)) return c;
        if (!fallback) fallback = c;
      }
      return fallback;
    },
    showLauncher() {
      return !this.hideOnPaths.includes(this.$route.path);
    },
    mentionList() {
      const q = (this.mention.query || '').toLowerCase();
      let list = this.assetCache;
      if (q) {
        list = list.filter(
          (a) => a.name.toLowerCase().includes(q) || (a.sub && a.sub.toLowerCase().includes(q))
        );
      }
      return list.slice(0, 50);
    },
  },
  watch: {
    // 侧边栏开关:切换 body 类名，让页面内容区自动收缩避免被遮挡
    visible(val) {
      if (val) {
        document.body.classList.add('ai-sidebar-open');
      } else {
        document.body.classList.remove('ai-sidebar-open');
      }
    },
    // 路由切换:选区元素已销毁,移除 region 类 chips(资产/图片 chips 仍有效)
    '$route.fullPath'() {
      this.stopPicking();
      // 页面元素已销毁:所有会话的 region 类 chips 一并失效(资产/图片 chips 仍有效)
      this.sessions.forEach((sx) => { sx.chips = (sx.chips || []).filter((c) => c.kind !== 'region'); });
    },
    // 输入内容变化(打字/粘贴/chip ⤵ 填入/@插入)时自动调整输入框高度
    inputText() {
      this.$nextTick(this.autoGrowInput);
    },
  },
  created() {
    this.restoreMessages();
    if (!this.sessions.length) this.sessions.push(this._newSessionObj());
    if (!this.activeSession) this.activeSessionId = this.sessions[0].id;
    aiBus.$on('ai-sidebar:open', this.onBusOpen);
  },
  mounted() {
    this._onMouseMove = (e) => this.handlePickMouseMove(e);
    this._onClickCapture = (e) => this.handlePickClick(e);
    this._onKeydown = (e) => {
      if (e.key === 'Escape' && this.picking) {
        e.stopPropagation();
        this.stopPicking();
      }
    };
    // 视口变化时重算输入框高度上限
    this._onWinResize = () => this.autoGrowInput();
    window.addEventListener('resize', this._onWinResize);
  },
  beforeDestroy() {
    aiBus.$off('ai-sidebar:open', this.onBusOpen);
    this.detachPickListeners();
    window.removeEventListener('resize', this._onWinResize);
    (this._imgEditTimers || []).forEach((t) => clearTimeout(t));
    document.body.classList.remove('ai-sidebar-open');
  },
  methods: {
    /* ============ 开关 ============ */
    open() {
      this.visible = true;
      this.mockOn = isAiMockEnabled();
      this.$nextTick(() => {
        this.scrollToBottom();
        this.autoGrowInput();
      });
    },
    close() {
      this.visible = false;
      this.stopPicking();
    },
    focusInput() {
      this.$nextTick(() => {
        try { const el = this.$refs.input; if (el && el.focus) el.focus(); } catch (e) { /* noop */ }
      });
    },
    onBusOpen(payload) {
      this.open();
      if (payload && payload.text) this.inputText = payload.text;
      // 来自页面卡片上的「编辑」小按钮:打开后把该选区选入上下文
      if (payload && payload.selectEl) {
        this.$nextTick(() => {
          try {
            const region = resolveAiRegion(payload.selectEl);
            if (!region) return;
            this.addRegionChip(region);
            // 聚焦输入框等待指令
            if (payload.editImage) {
              this.focusInput();
            }
          } catch (e) { /* 元素可能已卸载,忽略 */ }
        });
      }
    },

    /* ============ 选取模式 ============ */
    togglePicking() {
      this.picking ? this.stopPicking() : this.startPicking();
    },
    startPicking() {
      this.picking = true;
      this.pickedCountInSession = 0;
      document.body.classList.add('ai-picking');
      document.addEventListener('mousemove', this._onMouseMove, true);
      document.addEventListener('click', this._onClickCapture, true);
      document.addEventListener('mousedown', this._onClickCapture, true);
      document.addEventListener('keydown', this._onKeydown, true);
    },
    stopPicking() {
      if (!this.picking) return;
      this.picking = false;
      this.highlight.visible = false;
      document.body.classList.remove('ai-picking');
      this.detachPickListeners();
    },
    detachPickListeners() {
      document.removeEventListener('mousemove', this._onMouseMove, true);
      document.removeEventListener('click', this._onClickCapture, true);
      document.removeEventListener('mousedown', this._onClickCapture, true);
      document.removeEventListener('keydown', this._onKeydown, true);
      document.body.classList.remove('ai-picking');
    },
    _isInOwnUi(target) {
      return !!(target && target.closest && target.closest('.ai-pickbar, .ai-panel, .ai-launcher'));
    },
    handlePickMouseMove(e) {
      if (!this.picking) return;
      if (this._isInOwnUi(e.target)) {
        this.highlight.visible = false;
        return;
      }
      const region = findAiRegionFromPoint(e.clientX, e.clientY);
      if (region) {
        const rect = region.el.getBoundingClientRect();
        this.highlight = {
          visible: true,
          top: rect.top - 2,
          left: rect.left - 2,
          width: rect.width + 4,
          height: rect.height + 4,
          label: region.label,
          isImage: !!region.image && !region.text,
        };
        return;
      }
      // 回退:页面任意图片直接拾取(无需注册)
      const img = findPickableImageFromPoint(e.clientX, e.clientY);
      if (img) {
        const rect = img.el.getBoundingClientRect();
        this.highlight = {
          visible: true,
          top: rect.top - 2,
          left: rect.left - 2,
          width: rect.width + 4,
          height: rect.height + 4,
          label: '图片 · ' + img.label,
          isImage: true,
        };
        return;
      }
      this.highlight.visible = false;
    },
    handlePickClick(e) {
      if (!this.picking) return;
      if (this._isInOwnUi(e.target)) return; // 自己的 UI 正常交互
      // 阻断页面原有点击行为(避免误触发编辑弹窗/跳转)
      e.preventDefault();
      e.stopPropagation();
      if (e.type !== 'click') return; // mousedown 只负责拦截
      const region = findAiRegionFromPoint(e.clientX, e.clientY);
      if (region) {
        this.addRegionChip(region);
        this.pickedCountInSession += 1;
        this.stopPicking(); // 选中后自动完成选取,避免用户忘记点「完成」滞留在选取态
        return;
      }
      // 回退:页面任意图片直接拾取
      const img = findPickableImageFromPoint(e.clientX, e.clientY);
      if (img && this.addImageChip(img)) {
        this.pickedCountInSession += 1;
        this.stopPicking(); // 同上:成功拾取一张图后自动结束选取(本地/重复图不结束,便于改选)
      }
    },
    /** 拾取到的独立图片 → image chip(发送时进入 images 多模态输入) */
    addImageChip(img) {
      if (!img || !img.url) return false;
      if (img.isLocal) {
        this.$message({
          type: 'warning',
          showClose: true,
          message: '该图片是本地临时资源(blob/data),模型无法访问;请选取已上传的图片',
        });
        return false;
      }
      const exist = this.contextChips.find((c) => c.kind === 'image' && c.image === img.url);
      if (exist) {
        this.$message.info('该图片已在上下文中');
        return false;
      }
      _chipSeq += 1;
      this.contextChips.push({
        key: 'chip_' + _chipSeq,
        kind: 'image',
        label: img.label || '页面图片',
        text: '',
        image: img.url,
      });
      return true;
    },
    addRegionChip(region) {
      // 多镜头场景:展开为各子分镜独立选区,各自拥有 apply 回调
      if (region.children && region.children.length) {
        const addedLabels = [];
        region.children.forEach((child, index) => {
          const resolved = resolveAiRegion(region.el, child.key || index);
          if (!resolved) return;
          this.addRegionChip(resolved);
          addedLabels.push(child.label);
        });
        if (addedLabels.length) {
          this.$message.info('已添加 ' + addedLabels.length + ' 个子选区');
        }
        return;
      }
      // 同一选区(同元素)不重复添加,改为刷新内容
      const exist = this.contextChips.find((c) => c.kind === 'region' && c.regionId === region.id);
      if (exist) {
        exist.el = region.el;
        exist.childKey = region.childKey;
        exist.text = region.text;
        exist.image = region.image;
        exist.label = region.label;
        exist.canApply = region.canApply;
        exist.apply = region.apply;
        exist.applyTarget = region.applyTarget || null;
        this.$message.info('该区域已在上下文中,内容已刷新');
        return;
      }
      _chipSeq += 1;
      this.contextChips.push({
        key: 'chip_' + _chipSeq,
        kind: 'region',
        el: region.el,
        regionId: region.id,
        childKey: region.childKey,
        type: region.type,
        label: region.label,
        text: region.text,
        image: region.image,
        canApply: region.canApply,
        apply: region.apply,
        applyTarget: region.applyTarget || null,
      });
    },

    /* ============ chips ============ */
    chipIcon(chip) {
      if (chip.kind === 'asset') return 'el-icon-collection-tag';
      if (chip.image && !chip.text) return 'el-icon-picture-outline';
      return 'el-icon-document';
    },
    chipTitle(chip) {
      if (chip.text) return chip.text.slice(0, 300);
      if (chip.image) return chip.image;
      return chip.label;
    },
    removeChip(chip) {
      const i = this.contextChips.indexOf(chip);
      if (i !== -1) this.contextChips.splice(i, 1);
    },
    insertChipText(chip) {
      if (!chip.text) return;
      this.inputText = this.inputText ? this.inputText + '\n' + chip.text : chip.text;
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
      // 一次性提示:取出→手改→⤴ 回填 的闭环用法
      if (chip.kind === 'region' && chip.canApply && !this._wbHintShown) {
        this._wbHintShown = true;
        this.$message.info('原文已填入输入框;手动改完后点该 chip 上的 ⤴ 可直接回填更新原区域');
      }
    },
    /**
     * ⤴ 回填:把输入框当前内容直接写回该选区(不经过 AI、不耗积分)。
     * 走的是与「应用修改」完全相同的 apply() 链路:
     * edit 页选区即时落库(原保存方法弹提示),弹窗表单类选区只填表单由用户确认保存。
     */
    async writeBackChip(chip) {
      if (chip.applying) return;
      const text = this.inputText.trim();
      if (!text) {
        this.$message({ type: 'warning', showClose: true, message: '输入框为空,没有可回填的内容;可先点 chip 上的 ⤵ 取出原文编辑' });
        return;
      }
      // 回填前刷新选区,拿到最新的写回入口(页面/弹窗状态可能已变化)
      const fresh = refreshAiRegion(chip);
      chip.canApply = !!(fresh && fresh.canApply);
      chip.apply = fresh ? fresh.apply : null;
      if (!chip.canApply || !chip.apply) {
        this.$message({ type: 'warning', showClose: true, message: '该区域当前不可写回(可能已离开页面或弹窗已关闭),请重新选取' });
        return;
      }
      this.$set(chip, 'applying', true);
      try {
        const ok = await chip.apply(text);
        if (ok === false) {
          this.$message({ type: 'error', showClose: true, message: '回填失败,请重试' });
        } else if (chip.el && isAiRegionAlive(chip.el)) {
          // 成功:刷新 chip 缓存文本为最新值(成功提示由页面自己的保存逻辑弹出)
          const after = refreshAiRegion(chip);
          if (after) chip.text = after.text;
        }
      } catch (err) {
        console.error('[AiSidebar] 回填失败:', err);
        this.$message({ type: 'error', showClose: true, message: '回填失败: ' + ((err && err.message) || '未知错误') });
      } finally {
        this.$set(chip, 'applying', false);
      }
    },

    /* ============ 图像编辑(intent=edit_image)派发 ============ */
    /** 解析「素材编辑」模型(与编辑图像按钮同源:getCreateModels type=image 按 name 匹配),会话级缓存 */
    async _resolveEditImageModel() {
      if (this._editImageModel) return this._editImageModel;
      const res = await getCreateModels({ type: 'image' });
      const models = (res && (res.data || res)) || [];
      const found = Array.isArray(models) ? models.find((m) => m && m.name === '素材编辑') : null;
      if (!found) throw new Error('未找到「素材编辑」模型,请检查模型配置');
      this._editImageModel = found;
      return found;
    },
    /** 与 SceneEditDialog 同逻辑:按模型参数模板组装任务参数;无模板时回退 {image, value} */
    _buildImageEditParams(model, refImages, instruction) {
      const taskParams = {};
      if (model && Array.isArray(model.params) && model.params.length) {
        let imgIdx = 0;
        model.params.forEach((p) => {
          const type = ((p && p.type) || '').toLowerCase();
          const key = (p && (p.apiParamKey || p.paramKey || p.apiParam)) || '';
          if (type === 'file' || type === '文件上传') {
            if (imgIdx < refImages.length) taskParams[key || 'image'] = refImages[imgIdx];
            imgIdx++;
          } else if (type === 'textarea' || type === 'text' || type === 'input' || type === '长文本输入') {
            taskParams[key || 'value'] = instruction;
          } else if (p && p.defaultValue !== undefined && p.defaultValue !== null && p.defaultValue !== '') {
            taskParams[key] = p.defaultValue;
          }
        });
      }
      if (!Object.keys(taskParams).length) {
        taskParams.value = instruction;
        taskParams.image = refImages[0];
      }
      return taskParams;
    },
    /** 用户点「开始编辑」:提交生图任务并轮询(MOCK 下仅演练流程,不调接口不扣积分) */
    async runImageEdit(card) {
      if (!card || card.status === 'running' || card.status === 'done') return;
      this.$set(card, 'error', '');
      this.$set(card, 'status', 'running');
      this.persistMessages();
      if (this.mockOn) {
        setTimeout(() => {
          this.$set(card, 'resultUrl', card.image);
          this.$set(card, 'status', 'done');
          this.persistMessages();
          this.$message.success('(MOCK)图像编辑流程演练完成,结果为原图回显,未调用真实模型');
        }, 2000);
        return;
      }
      try {
        const model = await this._resolveEditImageModel();
        const refs = [card.image].concat(card.references || []);
        const res = await createGenerateTask({
          model: model.id || model.key || model.name,
          prompt: card.instruction,
          referenceImages: refs,
          params: this._buildImageEditParams(model, refs, card.instruction),
          context: { scriptId: this.$route.query.id, source: 'ai_sidebar' },
        });
        const tasks = (res && res.data && res.data.tasks) || (res && res.tasks) || [];
        const taskId = (tasks[0] && tasks[0].taskId) || (res && res.data && res.data.taskId) || (res && res.taskId);
        if (!taskId) throw new Error('任务提交失败:未获取到任务ID');
        this.$set(card, 'taskId', taskId);
        this.$message.success('图像编辑任务已提交,生成中…');
        this._pollImageEdit(card, taskId, 0);
      } catch (err) {
        console.error('[AiSidebar] 图像编辑提交失败:', err);
        this.$set(card, 'status', 'failed');
        this.$set(card, 'error', (err && (err.message || (err.response && err.response.data && err.response.data.message))) || '图像编辑失败,请重试');
        this.persistMessages();
      }
    },
    /* ============ 图片快捷模型标签(直点调用) ============ */
    /** 按 id 优先、名称兜底解析生图模型(getCreateModels type=image),会话级缓存 */
    async _resolveImageModel(id, name, cacheKey) {
      this._imgModelCache = this._imgModelCache || {};
      if (this._imgModelCache[cacheKey]) return this._imgModelCache[cacheKey];
      const res = await getCreateModels({ type: 'image' });
      const models = (res && (res.data || res)) || [];
      const found = (Array.isArray(models) && (
        models.find((m) => m && String(m.id) === String(id)) ||
        models.find((m) => m && m.name === name)
      )) || null;
      if (!found) throw new Error('未找到「' + name + '」模型(id=' + id + '),请检查模型配置');
      this._imgModelCache[cacheKey] = found;
      return found;
    },
    /** ★ 强化真人效果(id=97):无参数,直接发任务 */
    runQuickEnhance() {
      const target = this.quickImageTarget;
      if (!target || this.quickBusy.enhance) return;
      console.log('[AiSidebar:quickEnhance] target.kind:', target.kind, 'el:', !!target.el, 'applyTarget:', JSON.stringify(target.applyTarget), 'image:', target.image ? target.image.substring(0, 60) + '...' : 'null');
      this._startQuickImageTask({
        busyKey: 'enhance',
        modelId: 97,
        modelName: '真人效果加强',
        prompt: '强化真人效果',
        params: { image: target.image },
        introText: '已发起「强化真人效果」,目标:' + (target.label || '选中图片') + '。',
        image: target.image,
        sourceEl: this._imageSourceEl(target.el),
        regionEl: target.el || null,
        applyTarget: target.applyTarget || null,
      });
    },
    toggleCnForm() {
      if (this.quickBusy.cn) return;
      this.cnFormVisible = !this.cnFormVisible;
    },
    /** ★ 国人高质量真人(id=119):年龄+性别必填(与角色弹窗一致),姓名可选 */
    runQuickCnEnhance() {
      const target = this.quickImageTarget;
      if (!target || this.quickBusy.cn) return;
      if (!this.cnForm.age || !this.cnForm.gender) {
        this.$message.warning('请填写年龄并选择性别');
        return;
      }
      this.cnFormVisible = false;
      const params = { image: target.image, age: this.cnForm.age, gender: this.cnForm.gender };
      if (this.cnForm.name && this.cnForm.name.trim()) params.name = this.cnForm.name.trim();
      this._startQuickImageTask({
        busyKey: 'cn',
        modelId: 119,
        modelName: '国人高质量真人',
        prompt: '国人高质量真人',
        params,
        introText: '已发起「国人高质量真人」(' + (params.name ? '姓名 ' + params.name + '、' : '') + '年龄 ' + params.age + '、' + params.gender + '),目标:' + (target.label || '选中图片') + '。',
        image: target.image,
        sourceEl: this._imageSourceEl(target.el),
        regionEl: target.el || null,
        applyTarget: target.applyTarget || null,
      });
    },
    /** 推一条带任务卡片的助手消息并提交任务;payload 形态与角色/场景页按钮完全一致 */
    async _startQuickImageTask(opt) {
      const sess = this.activeSession;
      if (!sess) return;
      this.$set(this.quickBusy, opt.busyKey, true);
      // 实时从活跃选区获取最新的 applyTarget（chip 创建时 scriptId 可能尚未加载）
      let freshApplyTarget = opt.applyTarget || null;
      let freshSourceEl = opt.sourceEl || null;
      const regionEl = freshSourceEl || opt.regionEl || null;
      if (regionEl && isAiRegionAlive(regionEl)) {
        const fresh = resolveAiRegion(regionEl);
        if (fresh) {
          if (fresh.applyTarget) freshApplyTarget = fresh.applyTarget;
          // 若 _imageSourceEl 之前返回 null 但选区实际支持 applyImage，补上 sourceEl
          if (!freshSourceEl && fresh.canApplyImage) freshSourceEl = regionEl;
        }
      }
      console.log('[AiSidebar:quickTask] sourceEl:', !!freshSourceEl, 'applyTarget:', JSON.stringify(freshApplyTarget));
      const card = {
        image: opt.image,
        instruction: opt.prompt,
        references: [],
        status: 'running',
        resultUrl: '',
        error: '',
        taskId: '',
        modelLabel: opt.modelName + '(' + opt.modelId + ')',
        sourceEl: freshSourceEl,
        applyTarget: freshApplyTarget,
        replaced: false,
        replacing: false,
        // 重新生成配方:用「当前生效图」再次执行同款模型
        rerun: { type: 'quick', busyKey: opt.busyKey, modelId: opt.modelId, modelName: opt.modelName, prompt: opt.prompt, params: opt.params || {} },
      };
      _msgSeq += 1;
      sess.messages.push({
        id: 'm' + _msgSeq,
        role: 'assistant',
        streaming: false,
        rawText: '',
        text: opt.introText + (this.mockOn ? '(MOCK 演练,不调接口不扣积分)' : ''),
        error: '',
        edits: [],
        imageEdit: card,
      });
      if (/^会话 /.test(sess.title)) sess.title = opt.modelName.slice(0, 12);
      if (this.activeSessionId === sess.id) this.scrollToBottom();
      this.persistMessages();
      if (this.mockOn) {
        setTimeout(() => {
          this.$set(card, 'resultUrl', card.image);
          this.$set(card, 'status', 'done');
          this.$set(this.quickBusy, opt.busyKey, false);
          this.persistMessages();
          this.$message.success('(MOCK)「' + opt.modelName + '」流程演练完成,结果为原图回显');
        }, 2000);
        return;
      }
      try {
        const model = await this._resolveImageModel(opt.modelId, opt.modelName, opt.busyKey);
        const res = await createGenerateTask({
          model: model.id || model.key || model.name,
          prompt: opt.prompt,
          params: opt.params,
          context: { scriptId: this.$route.query.id, source: 'ai_sidebar_quick' },
        });
        const tasks = (res && res.data && res.data.tasks) || (res && res.tasks) || [];
        const taskId = (tasks[0] && tasks[0].taskId) || (res && res.data && res.data.taskId) || (res && res.taskId);
        if (!taskId) throw new Error('任务提交失败:未获取到任务ID');
        this.$set(card, 'taskId', taskId);
        this.$message.success('「' + opt.modelName + '」任务已提交,生成中…');
        this._pollImageEdit(card, taskId, 0);
      } catch (err) {
        console.error('[AiSidebar] 快捷模型任务提交失败:', err);
        this.$set(card, 'status', 'failed');
        this.$set(card, 'error', (err && (err.message || (err.response && err.response.data && err.response.data.message))) || '任务提交失败,请重试');
        this.persistMessages();
      } finally {
        this.$set(this.quickBusy, opt.busyKey, false);
      }
    },

    /** 仅当来源选区存活且支持图片写回(canApplyImage)时返回其 el,否则 null(按钮据此显隐) */
    _imageSourceEl(el) {
      if (!el || !isAiRegionAlive(el)) return null;
      const r = resolveAiRegion(el);
      return r && r.canApplyImage ? el : null;
    },
    /** 「应用替换」:把结果图写回来源选区(角色/场景/道具卡等支持 applyImage 的来源) */
    async applyImageResult(card) {
      if (!card || !card.resultUrl || card.replaced || card.replacing) return;
      // ① 来源页面仍存活:走选区闭包(本地 UI 即时更新 + 页面自己的落库逻辑)
      let applyImage = null;
      console.log('[AiSidebar:apply] 开始, sourceEl:', !!card.sourceEl, 'alive:', card.sourceEl ? isAiRegionAlive(card.sourceEl) : 'N/A', 'applyTarget:', JSON.stringify(card.applyTarget));
      if (card.sourceEl && isAiRegionAlive(card.sourceEl)) {
        const fresh = resolveAiRegion(card.sourceEl);
        console.log('[AiSidebar:apply] resolveAiRegion 结果:', fresh ? { type: fresh.type, label: fresh.label, canApplyImage: fresh.canApplyImage, hasApplyImage: !!fresh.applyImage } : 'null');
        if (fresh && fresh.applyImage) applyImage = fresh.applyImage;
      }
      this.$set(card, 'replacing', true);
      try {
        if (applyImage) {
          // ⚠️ 必须在 applyImage 之前解析 applyTarget！
          // 因为 applyImage 会把角色 avatar 改成新 URL，之后再用旧 URL 反查就找不到了
          console.warn('[AiSidebar:apply] ★★★ V3 前置解析开始 card.image:', card.image ? card.image.substring(0, 80) : 'NULL', 'card.applyTarget:', JSON.stringify(card.applyTarget));
          let preResolvedTarget = (card.applyTarget && card.applyTarget.scriptId && card.applyTarget.id) ? card.applyTarget : null;
          console.warn('[AiSidebar:apply] ① card.applyTarget:', JSON.stringify(preResolvedTarget));
          if (!preResolvedTarget && card.sourceEl && isAiRegionAlive(card.sourceEl)) {
            const freshBefore = resolveAiRegion(card.sourceEl);
            if (freshBefore && freshBefore.applyTarget) preResolvedTarget = freshBefore.applyTarget;
          }
          console.warn('[AiSidebar:apply] ② region.applyTarget:', JSON.stringify(preResolvedTarget));
          if (!preResolvedTarget) {
            preResolvedTarget = findApplyTargetByImage(card.image);
            console.warn('[AiSidebar:apply] ③ findApplyTargetByImage 结果:', JSON.stringify(preResolvedTarget));
          }
          if (!preResolvedTarget) {
            preResolvedTarget = resolveImageApplyTarget(card.image);
            console.warn('[AiSidebar:apply] ④ resolveImageApplyTarget 结果:', JSON.stringify(preResolvedTarget));
          }

          console.log('[AiSidebar:apply] 走 Path ① applyImage 闭包');
          const ok = await applyImage(card.resultUrl);
          console.log('[AiSidebar:apply] Path ① 返回:', ok);
          if (ok === false) {
            this.$message({ type: 'error', showClose: true, message: '替换失败,请重试' });
          } else {
            this.$set(card, 'replaced', true);
            // Path ① 完成前端更新后，用之前预解析的 applyTarget 确保后端持久化
            if (preResolvedTarget && preResolvedTarget.scriptId && preResolvedTarget.id) {
              console.log('[AiSidebar:apply] Path ① 后追加后端保存, target:', JSON.stringify(preResolvedTarget));
              try {
                await this._saveImageToTarget(preResolvedTarget, card.resultUrl);
                console.log('[AiSidebar:apply] 后端保存成功');
              } catch (saveErr) {
                console.error('[AiSidebar:apply] 后端保存失败:', saveErr);
              }
            } else {
              console.warn('[AiSidebar:apply] Path ① 成功但无 applyTarget，无法确保后端持久化');
            }
          }
          return;
        }
        // ② 凭卡片自带的数据级标识;③ 标识缺失(历史卡片/跨页面选取)时,按结果对应的原图 URL 反查当前页面归属
        let t = (card.applyTarget && card.applyTarget.scriptId && card.applyTarget.id) ? card.applyTarget : null;
        console.log('[AiSidebar:apply] Path ① 不可用, applyTarget:', JSON.stringify(card.applyTarget), '初始 t:', JSON.stringify(t));
        if (!t) {
          t = resolveImageApplyTarget(card.image);
          console.log('[AiSidebar:apply] resolveImageApplyTarget 结果:', JSON.stringify(t));
        }
        if (t && t.scriptId && t.id) {
          console.log('[AiSidebar:apply] 走 Path ② _saveImageToTarget, target:', JSON.stringify(t));
          await this._saveImageToTarget(t, card.resultUrl);
          this.$set(card, 'replaced', true);
          this.$message.success('已替换并保存到「' + (t.name || '目标资产') + '」;回到角色和场景页即可看到新图');
          return;
        }
        console.warn('[AiSidebar:apply] 所有路径均无法定位目标, card.image:', card.image);
        this.$message({ type: 'warning', showClose: true, message: '未能定位该图片归属;请在「角色和场景」页打开后重试,或「复制图片链接」手动更新' });
      } catch (err) {
        console.error('[AiSidebar] 应用替换失败:', err);
        this.$message({ type: 'error', showClose: true, message: '替换失败: ' + ((err && (err.response && err.response.data && err.response.data.message) || err.message)) || '未知错误' });
      } finally {
        this.$set(card, 'replacing', false);
        this.persistMessages();
      }
    },

    /** 重跑输入图:来源活着取实时图(最准) → 已替换取上次结果 → 兜底原目标图 */
    _currentAppliedImage(card) {
      if (card.sourceEl && isAiRegionAlive(card.sourceEl)) {
        const fresh = resolveAiRegion(card.sourceEl);
        if (fresh && fresh.image) return fresh.image;
      }
      if (card.replaced && card.resultUrl) return card.resultUrl;
      return card.image;
    },
    /** 「重新生成」:以当前生效图为输入再次执行同款模型,生成新卡片(旧卡保留作记录),可循环迭代 */
    regenerateImageTask(card) {
      if (!card || !card.rerun) {
        this.$message.warning('该任务缺少重跑配方(历史卡片),请重新选取图片发起');
        return;
      }
      const input = this._currentAppliedImage(card);
      if (!input) return;
      const liveSourceEl = this._imageSourceEl(card.sourceEl);
      if (card.rerun.type === 'quick') {
        const r = card.rerun;
        if (r.busyKey && this.quickBusy[r.busyKey]) {
          this.$message.info('「' + r.modelName + '」正在提交另一任务,请稍候');
          return;
        }
        this._startQuickImageTask({
          busyKey: r.busyKey || 'enhance',
          modelId: r.modelId,
          modelName: r.modelName,
          prompt: r.prompt,
          params: Object.assign({}, r.params, { image: input }),
          introText: '已基于当前生效图重新发起「' + r.modelName + '」。',
          image: input,
          sourceEl: liveSourceEl,
          applyTarget: card.applyTarget || null,
        });
        return;
      }
      // edit_image:同一编辑指令作用于当前生效图
      const sess = this.activeSession;
      if (!sess) return;
      const newCard = {
        image: input,
        instruction: card.instruction,
        references: (card.references || []).slice(),
        status: 'idle',
        resultUrl: '',
        error: '',
        taskId: '',
        modelLabel: card.modelLabel || '素材编辑',
        sourceEl: liveSourceEl,
        applyTarget: card.applyTarget || null,
        replaced: false,
        replacing: false,
        rerun: { type: 'edit_image' },
      };
      _msgSeq += 1;
      sess.messages.push({
        id: 'm' + _msgSeq,
        role: 'assistant',
        streaming: false,
        rawText: '',
        text: '已基于当前生效图重新发起图像编辑:' + String(card.instruction || '').slice(0, 40) + (this.mockOn ? '(MOCK 演练)' : ''),
        error: '',
        edits: [],
        imageEdit: newCard,
      });
      if (this.activeSessionId === sess.id) this.scrollToBottom();
      this.persistMessages();
      this.runImageEdit(newCard);
    },

    /** 把图片落库到写回目标:角色主图/场景/道具走通用端点,子形象走 form 端点精确更新 */
    async _saveImageToTarget(t, imageUrl) {
      if (t.kind === 'scene') {
        return updateScriptLocation(t.scriptId, t.id, { imageUrl });
      }
      if (t.kind === 'role-form' && t.formId) {
        return updateScriptCharacterForm(t.scriptId, t.id, t.formId, { imageUrl });
      }
      // role 与 prop 共用角色端点(与角色和场景页强化流程一致)
      const mainPromise = updateScriptCharacter(t.scriptId, t.id, { imageUrl });
      return mainPromise;
    },

    _pollImageEdit(card, taskId, retries) {
      if (retries > 120) {
        this.$set(card, 'status', 'failed');
        this.$set(card, 'error', '图像编辑超时,请稍后重试');
        this.persistMessages();
        return;
      }
      this._imgEditTimers = this._imgEditTimers || [];
      const t = setTimeout(async () => {
        try {
          const res = await getGenerateTask(taskId);
          const task = (res && res.data) || {};
          if (task.status === 'succeeded' && task.resultUrl) {
            this.$set(card, 'resultUrl', task.resultUrl);
            this.$set(card, 'status', 'done');
            this.persistMessages();
            this.$message.success('图像编辑完成');
            return;
          }
          if (task.status === 'failed') {
            this.$set(card, 'status', 'failed');
            this.$set(card, 'error', task.errorMessage || '图像编辑失败,请重试');
            this.persistMessages();
            return;
          }
        } catch (e) {
          console.warn('[AiSidebar] 图像编辑轮询异常(' + retries + '):', e && e.message);
        }
        this._pollImageEdit(card, taskId, retries + 1);
      }, 5000);
      this._imgEditTimers.push(t);
    },
    /**
     * 输入框随内容自动增高:
     * 高度跟随 scrollHeight,上限约 40% 视口高(最低 120px 兜底),超过上限后转为内部滚动。
     * 由 inputText watcher / open() / window resize 触发,覆盖打字、粘贴、chip ⤵ 填入、@插入全部路径。
     */
    autoGrowInput() {
      const el = this.$refs.input;
      if (!el || !this.visible) return;
      el.style.height = 'auto';
      const max = Math.max(120, Math.floor(window.innerHeight * 0.4));
      const h = Math.min(el.scrollHeight, max);
      el.style.height = h + 'px';
      el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
    },

    /* ============ @资产 ============ */
    assetTypeLabel(t) {
      return AI_ASSET_TYPE_LABEL[t] || '资产';
    },
    onInputChanged() {
      const el = this.$refs.input;
      if (!el) return;
      const pos = el.selectionStart;
      const before = this.inputText.slice(0, pos);
      const atIdx = before.lastIndexOf('@');
      if (atIdx === -1) return this.closeMention();
      const fragment = before.slice(atIdx + 1);
      // @ 后出现空白/换行则关闭
      if (/[\s\n]/.test(fragment)) return this.closeMention();
      // @ 必须在行首或空白后,避免邮箱等误触发
      const charBeforeAt = atIdx > 0 ? before[atIdx - 1] : '';
      if (charBeforeAt && !/[\s\n(（,，。:：]/.test(charBeforeAt)) return this.closeMention();
      if (!this.mention.visible) this.assetCache = collectAiAssets();
      this.mention.visible = true;
      this.mention.query = fragment;
      this.mention.triggerStart = atIdx;
      this.mention.activeIndex = 0;
    },
    onInputBlur() {
      // 延迟关闭,留出 mousedown 选择时间
      setTimeout(() => this.closeMention(), 150);
    },
    closeMention() {
      this.mention.visible = false;
      this.mention.query = '';
      this.mention.triggerStart = -1;
    },
    selectMention(item) {
      const el = this.$refs.input;
      const start = this.mention.triggerStart;
      if (el && start !== -1) {
        const pos = el.selectionStart;
        const before = this.inputText.slice(0, start);
        const after = this.inputText.slice(pos);
        this.inputText = before + '@' + item.name + ' ' + after;
      }
      // 资产 chip(去重)
      const exists = this.contextChips.some(
        (c) => c.kind === 'asset' && c.asset && c.asset.type === item.type && c.asset.id === item.id
      );
      if (!exists) {
        _chipSeq += 1;
        this.contextChips.push({
          key: 'chip_' + _chipSeq,
          kind: 'asset',
          label: this.assetTypeLabel(item.type) + '·' + item.name,
          image: item.avatar || '',
          text: '',
          asset: item,
        });
      }
      this.closeMention();
      this.$nextTick(() => el && el.focus());
    },
    onInputKeydown(e) {
      if (this.mention.visible && this.mentionList.length) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.mention.activeIndex = (this.mention.activeIndex + 1) % this.mentionList.length;
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.mention.activeIndex =
            (this.mention.activeIndex - 1 + this.mentionList.length) % this.mentionList.length;
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          this.selectMention(this.mentionList[this.mention.activeIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeMention();
          return;
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.send();
      }
    },

    /* ============ 发送 ============ */
    async send() {
      if (this.sending) return;
      const sess = this.activeSession; // 绑定发起时的会话:切换标签后流式/落盘仍写回本会话
      if (!sess) return;
      const userText = this.inputText.trim();
      if (!userText && !this.contextChips.length) return;

      // 发送前刷新选区快照(若元素仍存活,取最新内容)
      const regionChips = this.contextChips.filter((c) => c.kind === 'region');
      regionChips.forEach((c) => {
        const fresh = refreshAiRegion(c);
        if (fresh) {
          c.text = fresh.text;
          c.image = fresh.image;
          c.canApply = fresh.canApply;
          c.apply = fresh.apply;
          c.label = fresh.label;
          c.applyTarget = fresh.applyTarget || null;
        } else {
          c.canApply = false;
          c.apply = null;
        }
      });

      const regions = regionChips.map((c, i) => ({
        refKey: 'R' + (i + 1),
        type: c.type,
        label: c.label,
        text: c.text,
        image: c.image,
        canApply: c.canApply,
        apply: c.apply ? this.regionApplyCallback(c) : null,
      }));

      const imageChips = this.contextChips.filter((c) => c.kind === 'image');
      const images = imageChips.map((c, i) => ({ refKey: 'IMG' + (i + 1), label: c.label, url: c.image }));

      const assets = this.contextChips
        .filter((c) => c.kind === 'asset' && c.asset)
        .map((c) => c.asset);

      // 多模态图片输入:独立图片 + 选区附带图片 + 资产图片,去重限量
      const imageUrls = [];
      const pushImg = (u) => { if (u && imageUrls.indexOf(u) === -1) imageUrls.push(u); };
      images.forEach((i) => pushImg(i.url));
      regions.forEach((r) => pushImg(r.image));
      assets.forEach((a) => pushImg(a.avatar));

      const history = this.messages
        .filter((m) => !m.streaming && (m.text || m.role === 'user'))
        .map((m) => ({ role: m.role, text: m.text }));

      const content = buildAssistantContent({
        userText: userText || '(用户未输入文字,请根据选区内容给出优化建议)',
        regions,
        images,
        assets,
        history,
        pagePath: this.$route.fullPath,
      });

      // 用户消息上屏
      _msgSeq += 1;
      const ctxParts = [];
      if (regions.length) ctxParts.push('选区×' + regions.length);
      if (imageUrls.length) ctxParts.push('图片×' + imageUrls.length);
      if (assets.length) ctxParts.push('@' + assets.map((a) => a.name).join(' @'));
      sess.messages.push({
        id: 'm' + _msgSeq,
        role: 'user',
        text: userText || '(根据选区内容优化)',
        contextSummary: ctxParts.length ? '上下文: ' + ctxParts.join(' · ') : '',
      });
      // 默认标题的会话:用首条消息自动命名
      if (/^会话 /.test(sess.title)) sess.title = (userText || '图片分析').slice(0, 12);

      // 助手占位消息(流式)
      _msgSeq += 1;
      const assistantMsg = {
        id: 'm' + _msgSeq,
        role: 'assistant',
        streaming: true,
        rawText: '',
        text: '',
        error: '',
        edits: [],
        imageEdit: null,
      };
      sess.messages.push(assistantMsg);

      this.inputText = '';
      sess.sending = true;
      this.scrollToBottom();

      try {
        const raw = await callAiAssistant({
          content,
          images: imageUrls,
          scriptId: this.$route.query.id,
          onProgress: (text) => {
            assistantMsg.rawText = text;
            if (this.activeSessionId === sess.id) this.scrollToBottom();
          },
        });

        const parsed = parseAssistantOutput(raw);
        assistantMsg.text = parsed.reply || (parsed.edits.length ? '已生成修改建议:' : (parsed.imageEdit ? '已生成图像编辑指令:' : '(模型未返回内容)'));
        // intent=edit_image → 图像编辑指令卡片(目标图必须是本次发送过的图片,模型乱填时回退第一张)
        if (parsed.intent === 'edit_image' && parsed.imageEdit && imageUrls.length) {
          const ie = parsed.imageEdit;
          const image = imageUrls.indexOf(ie.image) !== -1 ? ie.image : imageUrls[0];
          const srcChip = this.contextChips.find((c) => c && c.image === image && (this._imageSourceEl(c.el) || c.applyTarget));
          assistantMsg.imageEdit = {
            image,
            instruction: ie.instruction,
            references: (ie.references || []).filter((u) => imageUrls.indexOf(u) !== -1 && u !== image),
            status: 'idle', // idle | running | done | failed
            resultUrl: '',
            error: '',
            taskId: '',
            sourceEl: srcChip ? this._imageSourceEl(srcChip.el) : null, // 来源选区(页面存活时本地+落库)
            applyTarget: srcChip ? (srcChip.applyTarget || null) : null, // 数据级标识(跨页面/刷新后仍可替换)
            replaced: false,
            replacing: false,
            rerun: { type: 'edit_image' },
          };
          // 自动开始编辑:识别到图像编辑意图即直接提交任务,不再等用户点「开始编辑」。
          // runImageEdit 内部已 try/catch(失败置 failed,卡片按钮转为「重试编辑」),此处 fire-and-forget 不阻塞 send。
          this.runImageEdit(assistantMsg.imageEdit);
        }
        assistantMsg.edits = parsed.edits.map((e, idx) => {
          const target = regions.find((r) => r.refKey === e.region) ||
            (regions.length === 1 && !e.region ? regions[0] : null) ||
            (idx < regions.length && !e.region ? regions[idx] : null); // 位置回退:仅在模型未标注 region 时生效
          return {
            regionRef: e.region,
            regionLabel: target ? target.label : (e.region || '未指明区域'),
            content: e.content,
            canApply: !!(target && target.canApply && target.apply),
            apply: target ? target.apply : null,
            applied: false,
            applying: false,
            expanded: false,
          };
        });
      } catch (err) {
        console.error('[AiSidebar] 推理失败:', err);
        assistantMsg.error = (err && err.message) || 'AI 调用失败,请重试';
      } finally {
        assistantMsg.streaming = false;
        assistantMsg.rawText = '';
        sess.sending = false;
        this.persistMessages();
        if (this.activeSessionId === sess.id) this.scrollToBottom();
      }
    },

    /* ============ 应用修改 ============ */
    regionApplyCallback(chip) {
      // 请求开始时固定选区身份，后续重新选取或 DOM 复用不能改变旧回复的目标。
      const snapshot = { el: chip.el, regionId: chip.regionId, childKey: chip.childKey };
      return (text) => {
        const fresh = refreshAiRegion(snapshot);
        if (!fresh || !fresh.apply) {
          this.$message.warning('原选区已变化或关闭，请重新选取后再应用修改');
          return false;
        }
        return fresh.apply(text);
      };
    },
    async applyEdit(edit) {
      if (!edit.apply || edit.applied || edit.applying) return;
      this.$set(edit, 'applying', true);
      try {
        const ok = await edit.apply(edit.content);
        if (ok === false) {
          this.$message({ type: 'error', showClose: true, message: '应用失败,请重试' });
        } else {
          this.$set(edit, 'applied', true);
        }
      } catch (e) {
        console.error('[AiSidebar] 应用修改失败:', e);
        this.$message({ type: 'error', showClose: true, message: '应用失败: ' + (e.message || '未知错误') });
      } finally {
        this.$set(edit, 'applying', false);
        this.persistMessages();
      }
    },

    /* ============ 杂项 ============ */
    copyText(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        this.$message.success('已复制');
      } catch (e) {
        this.$message.error('复制失败');
      }
      document.body.removeChild(ta);
    },
    /* ============ 多会话管理 ============ */
    _newSessionObj(title) {
      _sessionSeq += 1;
      return {
        id: 's' + Date.now() + '_' + _sessionSeq,
        title: title || ('会话 ' + (this.sessions.length + 1)),
        messages: [],
        chips: [],
        input: '',
        sending: false,
        picked: 0,
      };
    },
    addSession() {
      if (this.sessions.length >= 6) {
        this.$message.info('最多同时开 6 个会话');
        return;
      }
      const sx = this._newSessionObj();
      this.sessions.push(sx);
      this.activeSessionId = sx.id;
      this.closeMention();
      this.cnFormVisible = false;
      this.persistMessages();
      this.$nextTick(() => this.autoGrowInput());
    },
    switchSession(id) {
      if (id === this.activeSessionId) return;
      this.activeSessionId = id;
      this.closeMention();
      this.cnFormVisible = false;
      this.persistMessages();
      this.$nextTick(() => {
        this.scrollToBottom();
        this.autoGrowInput();
      });
    },
    /** 会话是否有进行中的请求/图片任务(标签上转圈) */
    sessionBusy(sx) {
      return !!(sx && (sx.sending || (sx.messages || []).some((m) => m.imageEdit && m.imageEdit.status === 'running')));
    },
    closeSession(id) {
      const idx = this.sessions.findIndex((sx) => sx.id === id);
      if (idx === -1) return;
      const target = this.sessions[idx];
      const doClose = () => {
        this.sessions.splice(idx, 1);
        if (!this.sessions.length) this.sessions.push(this._newSessionObj());
        if (this.activeSessionId === id) {
          this.activeSessionId = this.sessions[Math.min(idx, this.sessions.length - 1)].id;
        }
        this.persistMessages();
        this.$nextTick(() => this.scrollToBottom());
      };
      if (this.sessionBusy(target)) {
        this.$confirm('该会话还有进行中的任务,关闭后任务仍会在后台完成但结果不再展示。确定关闭?', '关闭会话', {
          type: 'warning',
          confirmButtonText: '关闭',
          cancelButtonText: '取消',
        }).then(doClose).catch(() => {});
      } else {
        doClose();
      }
    },

    clearMessages() {
      this.messages = [];
      this.persistMessages();
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.msgList;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    persistMessages() {
      try {
        const slimSessions = this.sessions.map((sx) => ({
          id: sx.id,
          title: sx.title,
          messages: this._slimMsgs(sx.messages),
        }));
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 2, activeId: this.activeSessionId, sessions: slimSessions }));
      } catch (e) { /* 忽略存储异常 */ }
    },
    _slimMsgs(list) {
      return (list || []).slice(-40).map((m) => ({
          id: m.id,
          role: m.role,
          text: m.text,
          error: m.error || '',
          contextSummary: m.contextSummary || '',
          edits: (m.edits || []).map((e) => ({
            regionRef: e.regionRef,
            regionLabel: e.regionLabel,
            content: e.content,
            applied: e.applied,
            canApply: false, // 刷新后写回闭包已失效,只保留复制能力
          })),
          imageEdit: m.imageEdit ? {
            image: m.imageEdit.image,
            instruction: m.imageEdit.instruction,
            references: m.imageEdit.references || [],
            // 刷新后轮询已断,进行中的任务降级为可重试
            status: m.imageEdit.status === 'running' ? 'failed' : m.imageEdit.status,
            resultUrl: m.imageEdit.resultUrl || '',
            error: m.imageEdit.status === 'running' ? '页面刷新导致任务状态丢失,可点「重试编辑」重新发起' : (m.imageEdit.error || ''),
            taskId: m.imageEdit.taskId || '',
            modelLabel: m.imageEdit.modelLabel || '',
            replaced: !!m.imageEdit.replaced,
            sourceEl: null, // 刷新后来源闭包失效
            applyTarget: m.imageEdit.applyTarget || null, // 数据级标识可序列化:刷新后「应用替换」仍可用
            rerun: m.imageEdit.rerun || null, // 重跑配方可序列化:刷新后「重新生成」仍可用
            replacing: false,
          } : null,
        }));
    },
    restoreMessages() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const data = JSON.parse(raw);
        const revive = (list) => (list || []).map((m) => ({
          ...m,
          streaming: false,
          rawText: '',
          edits: (m.edits || []).map((e) => ({ ...e, applying: false, expanded: false, apply: null })),
          imageEdit: m.imageEdit || null,
        }));
        let sessions = [];
        let activeId = '';
        if (Array.isArray(data)) {
          // 旧版单会话格式 → 迁移为会话 1
          _sessionSeq += 1;
          sessions = [{ id: 's_legacy_' + _sessionSeq, title: '会话 1', messages: revive(data), chips: [], input: '', sending: false, picked: 0 }];
          activeId = sessions[0].id;
        } else if (data && Array.isArray(data.sessions)) {
          sessions = data.sessions.map((sx, i) => ({
            id: sx.id || ('s_r' + i),
            title: sx.title || ('会话 ' + (i + 1)),
            messages: revive(sx.messages),
            chips: [],
            input: '',
            sending: false,
            picked: 0,
          }));
          activeId = data.activeId || (sessions[0] && sessions[0].id) || '';
        }
        if (sessions.length) {
          this.sessions = sessions;
          this.activeSessionId = activeId;
        }
        const all = sessions.reduce((acc, sx) => acc.concat(sx.messages || []), []);
        const maxId = all.reduce((mx, m) => {
          const n = parseInt(String(m.id || '').replace('m', ''), 10);
          return isNaN(n) ? mx : Math.max(mx, n);
        }, 0);
        _msgSeq = Math.max(_msgSeq, maxId);
      } catch (e) { /* 忽略 */ }
    },
  },
};
</script>

<style lang="scss" scoped>
$ai-primary: #7c3aed;
$ai-z: 1500;

/* ====== 唤起按钮 ====== */
.ai-launcher {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2500; /* 始终高于预览弹窗(2000)，确保在任何页面都可点击唤起 */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 8px;
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
  color: #fff;
  border-radius: 10px 0 0 10px;
  cursor: pointer;
  box-shadow: -2px 2px 10px rgba(109, 40, 217, 0.35);
  transition: padding 0.15s;
  i { font-size: 18px; }
  .ai-launcher-text { font-size: 11px; letter-spacing: 1px; }
  &:hover { padding-right: 14px; }
}

/* ====== 选取模式 ====== */
.ai-pickbar {
  position: fixed;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: $ai-z + 20;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: rgba(30, 27, 46, 0.92);
  color: #fff;
  font-size: 13px;
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  i { color: #c4b5fd; }
  .ai-pickbar-count {
    background: $ai-primary;
    border-radius: 999px;
    padding: 1px 8px;
    font-size: 12px;
  }
  .ai-pickbar-done {
    border: none;
    background: #fff;
    color: #4c1d95;
    border-radius: 999px;
    padding: 3px 12px;
    font-size: 12px;
    cursor: pointer;
    &:hover { background: #ede9fe; }
  }
}

.ai-pick-highlight {
  position: fixed;
  z-index: $ai-z + 10;
  pointer-events: none;
  border: 2px solid $ai-primary;
  border-radius: 6px;
  background: rgba(124, 58, 237, 0.08);
  box-shadow: 0 0 0 4000px rgba(15, 12, 30, 0.12);
  transition: top 0.05s linear, left 0.05s linear, width 0.05s linear, height 0.05s linear;

  .ai-pick-highlight-label {
    position: absolute;
    top: -26px;
    left: -2px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 360px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: $ai-primary;
    color: #fff;
    font-size: 12px;
    padding: 3px 8px;
    border-radius: 4px;
  }
}

/* ====== 面板 ====== */
.ai-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 92vw;
  z-index: $ai-z;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: -6px 0 24px rgba(17, 12, 46, 0.12);
  border-left: 1px solid #ece9f6;

  /* 选取模式下侧边栏保持原位，不滑出 */
}
.ai-slide-enter-active, .ai-slide-leave-active { transition: transform 0.25s ease; }
.ai-slide-enter, .ai-slide-leave-to { transform: translateX(110%); }

.ai-panel-header {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f1eef9;
  background: linear-gradient(135deg, #faf8ff, #f4f0ff);

  .ai-panel-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    color: #2d2150;
    i { color: $ai-primary; }
  }
  .ai-model-tag {
    font-size: 11px;
    font-weight: 400;
    color: $ai-primary;
    background: #ede9fe;
    border-radius: 4px;
    padding: 1px 6px;
  }
  .ai-panel-header-actions {
    display: flex;
    gap: 12px;
    i {
      cursor: pointer;
      color: #9a93b5;
      font-size: 15px;
      &:hover { color: $ai-primary; }
    }
  }
}

/* ====== 消息区 ====== */
.ai-msg-list {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  background: #fbfaff;
}
.ai-msg-empty {
  margin-top: 36px;
  text-align: center;
  color: #8b85a3;
  font-size: 13px;
  line-height: 1.8;
  .ai-msg-empty-tips { margin-top: 10px; font-size: 12px; color: #a9a3c0; b { color: $ai-primary; } }
}
.ai-msg { display: flex; margin-bottom: 12px; }
.ai-msg--user { justify-content: flex-end; }
.ai-msg--assistant { justify-content: flex-start; }
.ai-msg-bubble {
  max-width: 92%;
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 13px;
  line-height: 1.65;
  word-break: break-word;
}
.ai-msg--user .ai-msg-bubble {
  background: $ai-primary;
  color: #fff;
  border-bottom-right-radius: 3px;
}
.ai-msg--assistant .ai-msg-bubble {
  background: #fff;
  border: 1px solid #efeaf9;
  color: #3c3554;
  border-bottom-left-radius: 3px;
  width: 100%;
}
.ai-msg-ctx {
  font-size: 11px;
  opacity: 0.82;
  margin-bottom: 4px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.4);
  padding-bottom: 4px;
}
.ai-msg-text, .ai-msg-raw { white-space: pre-wrap; }
.ai-msg-raw { color: #9c96b6; font-size: 12px; max-height: 180px; overflow: hidden; margin-top: 6px; }
.ai-msg-streaming { color: $ai-primary; font-size: 12px; }
.ai-msg-error { color: #e25563; font-size: 12px; }

/* 修改建议卡片 */
.ai-edit-card {
  margin-top: 8px;
  border: 1px solid #e6defa;
  border-radius: 8px;
  overflow: hidden;
  background: #faf8ff;

  .ai-edit-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    background: #f1ebfe;
    font-size: 12px;
    .ai-edit-card-target { color: #5b21b6; font-weight: 600; i { margin-right: 2px; } }
    .ai-edit-applied { color: #16a34a; }
  }
  .ai-edit-card-content {
    padding: 8px 10px;
    font-size: 12.5px;
    white-space: pre-wrap;
    color: #443c5e;
    &.is-collapsed {
      max-height: 96px;
      overflow: hidden;
      position: relative;
      &::after {
        content: '';
        position: absolute;
        left: 0; right: 0; bottom: 0; height: 28px;
        background: linear-gradient(transparent, #faf8ff);
      }
    }
  }
  .ai-edit-card-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px 8px;
    .ai-edit-noapply { font-size: 11px; color: #b0aac6; margin-left: 6px; }
  }
}

/* ====== chips ====== */
.ai-chips {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-top: 1px solid #f1eef9;
  background: #fff;
  max-height: 96px;
  overflow-y: auto;
}
.ai-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 3px 8px;
  font-size: 12px;
  border-radius: 999px;
  border: 1px solid #e3dcf6;
  background: #f7f4ff;
  color: #4c4470;

  &.ai-chip--asset { background: #fef7ec; border-color: #f5e3c2; color: #92600a; }

  i { font-size: 13px; color: $ai-primary; }
  &.ai-chip--asset i { color: #d97706; }

  .ai-chip-img { width: 18px; height: 18px; border-radius: 4px; object-fit: cover; }
  .ai-chip-label {
    max-width: 180px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .ai-chip-insert { cursor: pointer; color: #a59ec4; &:hover { color: $ai-primary; } }
  .ai-chip-writeback { cursor: pointer; color: #a59ec4; &:hover { color: #16a34a; } }
  .ai-chip-remove { cursor: pointer; color: #b9b3d0; &:hover { color: #e25563; } }
}
/* ====== 会话 Tabs ====== */
.ai-tabs {
  flex: none;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px 0;
  overflow-x: auto;
  border-bottom: 1px solid #efeaf9;
  &::-webkit-scrollbar { height: 0; }
}
.ai-tab {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 150px;
  padding: 4px 8px 5px;
  border-radius: 8px 8px 0 0;
  font-size: 12px;
  color: #7a7396;
  background: #f3f0fb;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  border-bottom: none;
  &.is-active {
    background: #fff;
    color: #6d28d9;
    font-weight: 600;
    border-color: #efeaf9;
    margin-bottom: -1px;
  }
}
.ai-tab-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ai-tab-busy { color: #d97706; flex: none; }
.ai-tab-close {
  flex: none;
  font-size: 11px;
  color: #b9b3d0;
  border-radius: 50%;
  padding: 1px;
  &:hover { color: #e25563; background: #fdecee; }
}
.ai-tab-add {
  flex: none;
  font-size: 13px;
  color: #a59ec4;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  &:hover { color: #6d28d9; background: #f3f0fb; }
}

/* ====== 图片快捷模型标签 ====== */
.ai-quick-models {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 0;
  opacity: 0.72;
  transition: opacity 0.15s;
  &:hover { opacity: 1; }
}
.ai-quick-target {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid #e3dcf6;
  flex: none;
}
.ai-quick-tag {
  font-size: 11px;
  color: #fff;
  padding: 4px 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(234, 88, 12, 0.92), rgba(217, 119, 6, 0.92));
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  transition: transform 0.12s, box-shadow 0.12s;
  &:hover { transform: translateY(-1px); box-shadow: 0 3px 10px rgba(234, 88, 12, 0.35); }
  &.is-active { box-shadow: 0 0 0 2px rgba(234, 88, 12, 0.35); }
  &.is-busy { pointer-events: none; opacity: 0.45; }
}
.ai-quick-cnform {
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px 0;
  .ai-cn-name { width: 110px; }
  .ai-cn-age { width: 76px; }
}

/* ====== 图像编辑指令卡片 ====== */
.ai-imgedit-card {
  margin-top: 8px;
  border: 1px solid #e3dcf6;
  border-radius: 10px;
  background: #faf9ff;
  padding: 10px;
}
.ai-imgedit-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  color: #6d28d9;
  margin-bottom: 8px;
  i { margin-right: 4px; }
}
.ai-imgedit-model { font-weight: 400; color: #a59ec4; font-size: 11px; }
.ai-imgedit-body { display: flex; gap: 10px; align-items: flex-start; }
.ai-imgedit-thumb {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e3dcf6;
  background: #fff;
  flex: none;
}
.ai-imgedit-instruction {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 1.7;
  color: #3c3554;
  white-space: pre-wrap;
  word-break: break-word;
}
.ai-imgedit-result {
  margin-top: 10px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e8e4f3;
  box-shadow: 0 2px 8px rgba(80, 60, 120, 0.08);
  overflow: hidden;
}
.ai-imgedit-compare {
  display: flex;
  align-items: stretch;
  gap: 0;
}
.ai-imgedit-compare-item {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.ai-imgedit-compare-label {
  font-size: 11px;
  color: #888;
  text-align: center;
  padding: 6px 0 4px;
  background: #faf9ff;
}
.ai-imgedit-compare-img {
  width: 100%;
  height: 160px;
  object-fit: cover;
  display: block;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.92; }
}
.ai-imgedit-compare-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  flex: none;
  color: #b0a8d0;
  font-size: 16px;
  background: #faf9ff;
  border-left: 1px solid #f0ecf8;
  border-right: 1px solid #f0ecf8;
}
.ai-imgedit-result-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: #faf9ff;
  border-top: 1px solid #f0ecf8;
}
.ai-imgedit-done { font-size: 12px; color: #16a34a; i { margin-right: 2px; } }
.ai-imgedit-result-actions { display: inline-flex; align-items: center; gap: 4px; }
.ai-imgedit-actions { margin-top: 8px; text-align: right; }

.ai-chips-clear {
  font-size: 11px;
  color: #a9a3c0;
  cursor: pointer;
  &:hover { color: $ai-primary; }
}

/* ====== @mention ====== */
.ai-mention {
  flex: none;
  max-height: 220px;
  overflow-y: auto;
  border-top: 1px solid #f1eef9;
  background: #fff;
  box-shadow: 0 -6px 14px rgba(40, 24, 90, 0.06);
}
.ai-mention-empty { padding: 14px; font-size: 12px; color: #a9a3c0; text-align: center; }
.ai-mention-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  font-size: 13px;
  cursor: pointer;
  &.active { background: #f4f0ff; }

  .ai-mention-avatar {
    width: 26px; height: 26px;
    border-radius: 6px;
    object-fit: cover;
    flex: none;
    &--ph {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #ece8f8;
      color: #9b91c9;
    }
  }
  .ai-mention-type {
    flex: none;
    font-size: 11px;
    color: $ai-primary;
    background: #ede9fe;
    border-radius: 4px;
    padding: 0 5px;
  }
  .ai-mention-name { flex: none; color: #3c3554; font-weight: 500; }
  .ai-mention-sub {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 12px;
    color: #a9a3c0;
  }
}

/* ====== 输入区 ====== */
.ai-input-area {
  flex: none;
  border-top: 1px solid #f1eef9;
  background: #fff;
  padding: 8px 12px 12px;
}
.ai-input-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  .ai-input-hint { font-size: 11px; color: #b3adc9; }
}
.ai-input-row { display: flex; align-items: flex-end; gap: 8px; }
.ai-input {
  flex: 1;
  resize: none;
  border: 1px solid #e3dcf6;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.6;
  color: #3c3554;
  outline: none;
  font-family: inherit;
  /* 自动增高:JS 跟随内容设置 height,这里限定上下界;超过上限由 JS 切为内部滚动 */
  min-height: 64px;
  max-height: 40vh;
  overflow-y: hidden;
  box-sizing: border-box;
  &:focus { border-color: $ai-primary; box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.12); }
  &::placeholder { color: #b9b3d0; }
}
.ai-send-btn { flex: none; }
</style>

<style lang="scss">
/* 全局(非 scoped):选取模式下给所有已注册区域加可发现性虚线 */
body.ai-picking [data-ai-selectable] {
  outline: 1.5px dashed rgba(124, 58, 237, 0.45) !important;
  outline-offset: 2px;
  cursor: crosshair !important;
}
body.ai-picking { cursor: crosshair; }

/* ====== 侧边栏打开时页面内容区自动收缩 ====== */
body.ai-sidebar-open:not(.ai-picking) {
  padding-right: min(420px, 92vw);
  transition: padding-right 0.25s ease;
}
body.ai-sidebar-open:not(.ai-picking) .global-header-actions {
  right: calc(min(420px, 92vw) + 24px);
  transition: right 0.25s ease;
}
</style>
